import React from 'react'
// @src imports
import { Notification, Transition } from 'src/chrome'
import {
  getNotifications,
  getReminders,
  getSharedCampaigns,
  getSharedContacts,
  Maybe,
  MinimalReminderFragment,
  NotificationType,
} from 'src/graphql'
import { Reminder, ShareNotification } from 'src/dashboard/components'
import { useActions, useMutations, useQueries } from 'src/hooks'
import { AddOrderCardRoute } from 'src/orders/routes/AddOrderCardRoute'
// relative imports
import styles from './notifications.module.scss'
import suspenseBoundary from '../../../chrome/SuspenseBoundary/suspenseBoundaryHOC'
import { Spacer, Text } from '@sendoutcards/quantum-design-ui'

const Notifications: React.FC = () => {
  const actions = useActions()
  const mutations = useMutations()

  const [
    unfilteredReminders,
    notifications,
    campaignShares,
    contactShares,
  ] = useQueries(
    getReminders({ offset: 0, limit: 20 }),
    getNotifications(),
    getSharedCampaigns({ offset: 0, limit: 20 }),
    getSharedContacts({ offset: 0, limit: 20 }),
  )

  // Filter out reminders of type Project with no campaign.
  const reminders = unfilteredReminders.filter(
    reminder => !(reminder.type === 'PROJECT' && !reminder.campaign),
  )

  const shares = {
    campaigns: campaignShares,
    contacts: contactShares,
  }

  const handleDismissReminder = (reminder: MinimalReminderFragment) => {
    mutations.deleteReminder({ id: reminder.id })
  }

  const denyNotification = (id: string) => {
    mutations.denyNotification({ id: id })
  }

  const acceptContactShare = (notification: { id: string }) => {
    mutations.acceptSharedContacts({ id: notification.id })
  }

  const acceptCampaignShare = (notification: { id: string }) => {
    mutations.acceptCampaignShare({ id: notification.id })
  }

  const denyContactShare = (notification: { id: string }) => {
    mutations.deleteContactShare({ id: notification.id })
  }

  const denyCampaignShare = (notification: { id: string }) => {
    mutations.deleteCampaignShare({ id: notification.id })
  }

  const handleCreateOrder = async (contact: { id: string }) => {
    const {
      createOrder: { order },
    } = await mutations.createOrder({ order: { contacts: [contact.id] } })

    actions.openOrder(order.id, AddOrderCardRoute())
  }

  const handleViewCardHistory = (type: Maybe<NotificationType>) => {
    actions.openCardHistory(type === 'REJECTED_CARDS' ? 'Rejected' : 'Held')
  }

  const hasShareNotifications =
    (shares.campaigns && shares.campaigns.length > 0) ||
    (shares.contacts && shares.contacts.length > 0)

  return (
    <div className={styles.notificationsWrapper}>
      {shares.campaigns.map(notification => (
        <ShareNotification
          key={notification.id}
          notification={notification}
          title={'Shared Campaign'}
          sharedItemName={notification.campaign.name}
          dateShared={notification.dateShared}
          from={notification.fromName}
          onAccept={acceptCampaignShare}
          onDeny={denyCampaignShare}
        />
      ))}
      {shares.contacts.map(notification => (
        <ShareNotification
          key={notification.id}
          notification={notification}
          title={'Shared Contact'}
          sharedItemName={`${notification.contact.firstName} ${notification.contact.lastName}`}
          dateShared={notification.createdAt}
          from={`${notification.fromUser.firstName} ${notification.fromUser.lastName}`}
          onAccept={acceptContactShare}
          onDeny={denyContactShare}
        />
      ))}
      {!hasShareNotifications && !reminders.length && (
        <div className={styles.emptyTarget}>
          <Text type="body">No Messages...</Text>
        </div>
      )}
      {reminders.map(reminder => (
        <Reminder
          key={reminder.id}
          reminder={reminder}
          onSendCard={handleCreateOrder}
          onDismiss={handleDismissReminder}
          openCampaign={actions.openCampaign}
        />
      ))}
      {notifications.map(notification => (
        <Notification
          key={notification.id}
          message={notification.message ?? ''}
          onAccept={() => handleViewCardHistory(notification.type)}
          actionText={notification.linkText ?? 'Accept'}
          onClose={() => denyNotification(notification.id)}
        />
      ))}
      <Spacer space="x9" />
    </div>
  )
}

export default suspenseBoundary({
  component: Notifications,
  unresolved: <Transition message={'Loading notifications...'} />,
  failure: error => `Failed loading notifications: ${error}`,
})
